import {
  INSTALLMENT_STATUS,
  TRANSACTION_STATUS,
} from "../features/loans/types/loan";
import { getAge } from "./util";
import i18next from "i18next";
import { useFormProfileOptions } from "../constants/form-profile";
import { reverseMaritalStatus } from "../constants/form-profile";
import dayjs from "dayjs";

export function useSerializePayloadForLoanRegistration() {
  const { MARITAL_STATUS_TRANSLATIONS } = useFormProfileOptions();

  return (
    selectedLoan,
    formProfileData,
    firstFillFormData,
    secondFillFormData,
    thirdFillFormData,
    hasSignedContact,
    isMou,
    isForeigner
  ) => {
    const {
      borrower_id,
      additional_contacts,
      formValues: {
        currentAddress,
        currentProvince,
        currentDistrict,
        currentSubDistrict,
        currentPostalCode,
        status,
        children,
        education,
        idCardAddress,
        idCardProvince,
        idCardDistrict,
        idCardSubDistrict,
        idCardPostalCode,
      },
    } = formProfileData;

    const {
      current_company,
      job_name,
      income_type,
      job_experience_in_year,
      monthly_income,
      out_time_income,
      other_income,
      job_type,
      total_debt,
      monthly_debt,
      debt_amount,
      monthly_outcome,
      jobLevel,
      other_job_name,
      hire_type,
    } = firstFillFormData;

    const { loan_amount, installment_period, vehicle_type } = secondFillFormData;

    const { bank_name, bank_account } = thirdFillFormData;

    const address = isForeigner
      ? [
          {
            address_type: i18next.t("loan:status.address_type"),
            address_no: currentAddress,
            province: currentProvince,
            district: currentDistrict,
            sub_district: currentSubDistrict,
            zip: currentPostalCode,
          },
        ]
      : [
          {
            address_type: i18next.t("loan:status.address_type"),
            address_no: currentAddress,
            province: currentProvince,
            district: currentDistrict,
            sub_district: currentSubDistrict,
            zip: currentPostalCode,
          },
          {
            address_type: i18next.t("loan:status.address_type2"),
            address_no: idCardAddress,
            province: idCardProvince,
            district: idCardDistrict,
            sub_district: idCardSubDistrict,
            zip: idCardPostalCode,
          },
        ];

    const payload = {
      customer_type: 1,
      borrower_id,
      loan_type_id: isMou ? current_company : selectedLoan.id,
      loan_term: installment_period,
      loan_amount,
      vehicle_type,
      first_month_option: "principal_included", // TODO : Use real first month option
      // request_date: new Date().toISOString().split("T")[0],
      e_sign_contact: hasSignedContact,
      address,
      additional_contacts,
      job: {
        name: job_name,
        company_name: current_company,
        job_type,
        job_experience_in_year: parseFloat(job_experience_in_year) * 12, // :Convert to months
        level: jobLevel,
        other_job_detail: other_job_name,
        employment: hire_type,
      },
      income: {
        monthly_income,
        income_type,
        out_time_income,
        other_income,
      },
      outcome: {
        monthly_outcome,
        debt_amount,
        monthly_debt,
        total_debt,
      },
      bank_statement: {
        bank_name,
        bank_account,
        auto_payment: true, // TODO : Use real auto payment
      },
      general: {
        education,
        children,
        status: reverseMaritalStatus[status] ?? status,
      },
    };

    if (isMou) {
      payload.mou_company = current_company;
    } else {
      payload.mou_status = "reject";
    }

    return payload;
  };
}

export function getLoanAdvantagesText(loan) {
  if (!loan.advantages) return "";
  // Strip ALL HTML tags for plain text display
  return loan.advantages.replace(/<[^>]*>/g, "").trim();
}

export function getLoanAdvantagesHtml(loan) {
  if (!loan.advantages) return "";
  return loan.advantages;
}

export function getLoanMaxAge(loan) {
  return loan.condition.maxinum_age ?? 0;
}

export function getLoanMinAge(loan) {
  return loan.condition.mininum_age ?? 0;
}

export function getLoansMatchingUserCriteria(
  loans,
  incomeFormProfile,
  userData
) {
  if (!userData) return [];

  return loans?.filter((loan) => {
    const isProvinceEligible = loan.condition.province.includes(
      userData.province
    );

    const isUserAgeEligible =
      getAge(userData.date_of_birth) <= getLoanMaxAge(loan) &&
      getAge(userData.date_of_birth) >= getLoanMinAge(loan);

    return true;
  });
}

export function filterLoanStatus(loans, status) {
  return loans?.filter(
    (loan) => loan.loan_state === status || !Boolean(status)
  );
}

export function getUniqueLoanStatuses(loans) {
  const statues = loans?.map((loan) => loan.loan_state);

  return [...new Set(statues)];
}

export function getLoanSummary(loans) {
  const loanStatusesToCalculate = ["open"];

  if (!loans)
    return {
      totalLoanAmount: 0,
      totalDebtAmount: 0,
      totalPaidPrincipal: 0,
      totalPaidInterest: 0,
    };

  const filteredLoans = loans.filter((loan) =>
    loanStatusesToCalculate.includes(loan.loan_state)
  );

  const totalLoanAmount =
    filteredLoans.reduce((acc, loan) => acc + loan.loan_amount, 0) ?? 0;

  const totalPaidPrincipal = filteredLoans.reduce((acc, loan) => {
    return acc + loan.principle_amount_sum;
  }, 0);

  const totalPaidInterest = filteredLoans.reduce((acc, loan) => {
    return acc + loan.interest_amount_sum;
  }, 0);

  const totalDebtAmount =
    filteredLoans.reduce((acc, loan) => acc + loan.remaining_amount, 0) ?? 0;

  return {
    totalLoanAmount,
    totalDebtAmount,
    totalPaidPrincipal,
    totalPaidInterest,
  };
}

export function getLoanList(userLoanInfo) {
  return userLoanInfo?.loan_data ?? null;
}

// TODO : Finish this
export function formatBackAccount(bankAccount) {
  return bankAccount;
}

export function shouldShowLoanStatus(loan) {
  if (!loan) return false;
  const shouldNotShowLoanStatus = ["close", "open"];
  return !shouldNotShowLoanStatus.includes(loan.loan_state);
}

export function shouldShowLostPaid(loan) {
  const shouldShowLostPaidLoanStatus = ["open"];
  return loan && shouldShowLostPaidLoanStatus.includes(loan.loan_state);
}

export function shouldShowNextInstallment(loan) {
  const shouldNotShowNextInstallmentLoanStatus = [
    "close",
    "accept",
    "change_principle",
  ];
  return (
    loan && !shouldNotShowNextInstallmentLoanStatus.includes(loan.loan_state)
  );
}

export function shouldShowDebtAmount(loan) {
  const shouldShowDebtAmountLoanStatus = ["open"];
  return loan && shouldShowDebtAmountLoanStatus.includes(loan.loan_state);
}

export function shouldShowTotalInstallment(loan) {
  if (!loan) return false;
  const shouldShowTotalInstallmentLoanStatus = ["open"];
  return shouldShowTotalInstallmentLoanStatus.includes(loan.loan_state);
}

export function shouldShowPaidRound(loan) {
  if (!loan) return false;

  const shouldShowPaidRoundLoanStatus = ["accept", "disburse_process", "open"];

  return shouldShowPaidRoundLoanStatus.includes(loan.loan_state);
}

export function shouldShowLoanBankInfo(loan) {
  if (!loan) return false;
  const shouldNotShowLoanBankInfoLoanStatus = ["close", "open"];
  return !shouldNotShowLoanBankInfoLoanStatus.includes(loan.loan_state);
}

export function shouldShowTotalPaid(loan) {
  const shouldShowTotalPaidLoanStatus = ["approve", "disburse", "close"];
  return loan && shouldShowTotalPaidLoanStatus.includes(loan.loan_state);
}

export function showLoanStatusMessage(status) {
  switch (status) {
    case "request":
      return i18next.t("loan:loanStatus.request");
    case "draft":
      return i18next.t("loan:loanStatus.draft");
    case "close":
      return i18next.t("loan:loanStatus.close");
    case "open":
      return i18next.t("loan:loanStatus.open");
    case "document_reject":
      return i18next.t("loan:loanStatus.document_reject");
    case "disburse_process":
      return i18next.t("loan:loanStatus.disburse_process");
    case "disburse":
      return i18next.t("loan:loanStatus.disburse");
    case "accept":
      return i18next.t("loan:loanStatus.accept");
    case "approve":
      return i18next.t("loan:loanStatus.approve");
    case "cancel":
      return i18next.t("loan:loanStatus.cancel");
    case "change_principle":
      return i18next.t("loan:loanStatus.change_principle");
    case "score_confirm":
      return i18next.t("loan:loanStatus.score_confirm");
    case "reject":
      return i18next.t("loan:loanStatus.reject");
    case "document_confirm":
      return i18next.t("loan:loanStatus.document_confirm");
    default:
      return i18next.t("loan:loanStatus.default");
  }
}

export function getLoanStateColor(loanState) {
  switch (loanState) {
    case "request":
      return "#F0AC4C";
    case "score_confirm":
      return "#F0AC4C";
    case "document_confirm":
      return "#0089E4";
    case "document_reject":
      return "#B93815";
    case "reject":
      return "#B93815";
    case "change_principle":
      return "#17B26A";
    case "approve":
      return "#17B26A";
    case "disburse":
      return "#17B26A";
    default:
      return "#000000";
  }
}

export function shouldShowLoanContactExample(loanInfo) {
  const shouldShowLoanContactExampleLoanStates = ["disburse"];
  return (
    loanInfo &&
    shouldShowLoanContactExampleLoanStates.includes(loanInfo.loan_state)
  );
}

export function shouldShowCancelLoanButton(loanInfo) {
  const shouldShowCancelLoanButtonLoanStates = ["approve"];
  return (
    loanInfo &&
    shouldShowCancelLoanButtonLoanStates.includes(loanInfo.loan_state)
  );
}

export function shouldShowInstallmentTable(loanInfo) {
  const allowedLoanStates = ["open", "accept", "disburse_process"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowAcceptAgreementDocumentButton(loanInfo) {
  const allowedLoanStates = ["approve"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowAgreementDocumentInfoButton(loanInfo) {
  const allowedLoanStates = ["disburse", "close", "accept", "disburse_process"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowMakePaymentButton(loanInfo, installment) {
  const shouldShowMakePaymentLoanStates = ["open"];

  const shouldShowMakePaymentInstallmentStates = [
    "unpaid",
    "cancel",
    "notfullpaid",
    "pending",
  ];

  return (
    loanInfo &&
    shouldShowMakePaymentLoanStates.includes(loanInfo.loan_state) &&
    shouldShowMakePaymentInstallmentStates.includes(installment?.state) &&
    loanInfo?.mou_status !== "approve"
  );
}

export function shouldShowInvoiceCurrentButton(loanInfo) {
  const shouldShowInvoiceCurrentLoanStates = ["open"];
  return (
    loanInfo && shouldShowInvoiceCurrentLoanStates.includes(loanInfo.loan_state)
  );
}

export function shouldShowLoanDocumentList(loanInfo) {
  const allowedLoanStates = ["document_reject"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowEditLoanButton(loanInfo) {
  const allowedLoanStates = ["document_reject"];
  // const hasPendingDocument = loanInfo?.loan_documents?.some(
  //   (document) => document.review_status === "pending"
  // );
  return (
    loanInfo && allowedLoanStates.includes(loanInfo.loan_state)
    // !hasPendingDocument
  );
}

export function shouldShowCreateNewLoanButton(loanInfo) {
  const allowedLoanStates = ["reject", "cancel"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowReceiptButton(loanInfo) {
  const allowedLoanStates = ["close"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowContactButton(loanInfo) {
  const allowedLoanStates = [
    "document_reject",
    "change_principle",
    "request",
    "reject",
    "score_confirm",
    "disburse",
    "document_confirm",
    "accept",
    "disburse_process",
  ];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function shouldShowCheckNewLoanPrincipalButton(loanInfo) {
  const allowedLoanStates = ["change_principle"];
  return loanInfo && allowedLoanStates.includes(loanInfo.loan_state);
}

export function getLoanInstallmentProgress(loanInfo) {
  const paidInstallments = loanInfo.installments.filter(
    (loan) => loan.state === "paid"
  ).length;

  const totalInstallments = loanInfo.installments.length;

  return {
    paidInstallments,
    totalInstallments,
  };
}

export function getLoanInstallmentSummary(loanInfo) {
  const DEFAULT_VALUE = {
    totalPaid: 0,
    totalInterest: 0,
    totalInstallmentAmount: 0,
    paidInstallments: 0,
    totalInstallments: 0,
    totalRemainingAmount: 0,
  };

  if (!loanInfo || !loanInfo.installments) return DEFAULT_VALUE;

  const totalPaid = loanInfo.installments.reduce(
    (acc, installment) =>
      acc + (installment.state === "paid" ? installment.total_amount : 0),
    0
  );

  const totalInterest = loanInfo.installments.reduce(
    (acc, installment) =>
      acc + (installment.state === "paid" ? installment.interest : 0),
    0
  );

  const totalInstallmentAmount = loanInfo.loan_amount;

  console.log("totalInterest", totalInterest);

  const { paidInstallments, totalInstallments } =
    getLoanInstallmentProgress(loanInfo);

  return {
    totalPaid,
    totalInterest,
    totalInstallmentAmount,
    paidInstallments,
    totalInstallments,
    totalRemainingAmount: totalInstallmentAmount - totalPaid,
  };
}

export function getNextLoanInstallment(loanInfo) {
  const nextLoanInstallment = loanInfo.installments.find(
    (installment) => new Date(installment?.installment_date) > new Date()
  );

  return nextLoanInstallment;
}

export function getLoanInstallmentRemaining(installmentInfo) {
  const { interest_remaining, principal_remaining } = installmentInfo;

  return interest_remaining + principal_remaining;
}

export function getLoanInstallmentTotalInterest(installments) {
  console.log(installments);
  return installments.reduce(
    (acc, installment) => acc + installment.interest,
    0
  );
}

export function getLoanInstallmentTotalPrincipal(installments) {
  return installments.reduce(
    (acc, installment) => acc + installment.principal,
    0
  );
}

export function getCurrentInstallment(installments) {
  if (!installments) return null;

  const areAllInstallmentsUnpaid = installments.every(
    (installment) => installment.state === "unpaid"
  );

  if (areAllInstallmentsUnpaid) return installments[0];

  const lastPaidInstallmentIndex = installments.findLastIndex(
    (installment) => installment.state === "paid"
  );

  const lastPaidInstallment = installments[lastPaidInstallmentIndex];

  const isLastPaidInstallmentBeforeToday =
    new Date(lastPaidInstallment.paid_date) < new Date();

  if (isLastPaidInstallmentBeforeToday) {
    return installments[lastPaidInstallmentIndex + 1];
  }

  return lastPaidInstallment;
}

export function formatAttachment({
  loanId,
  attachment,
  fileStatus = undefined,
  fileHide = false,
}) {
  const formData = new FormData();

  const { name, file, documentId } = attachment;

  formData.append("loan_id", loanId);
  formData.append("file_name", name);
  formData.append("data", file);

  if (documentId) formData.append("document_id", documentId);

  if (fileStatus) formData.append("file_status", fileStatus);

  if (fileHide) formData.append("file_hide", fileHide);

  return formData;
}

export function convertFileInfoToAttachment(fileInfo, fileName = "") {
  const attachmentPayload = {
    name: fileName || fileInfo.file.name,
    file: fileInfo.file,
  };

  if (fileInfo.documentId) attachmentPayload.documentId = fileInfo.documentId;

  return attachmentPayload;
}

export function getAllAttachments({
  formProfileData,
  firstFillFormData,
  thirdFillFormData,
}) {
  const { attachment: formProfileAttachment } = formProfileData;
  const { attachment: firstFillFormAttachment } = firstFillFormData;
  const { attachment: thirdFillFormAttachment } = thirdFillFormData;

  return [
    ...formProfileAttachment,
    ...firstFillFormAttachment,
    ...thirdFillFormAttachment,
  ];
}

export function shouldShowThisLoan(loan) {
  const loanStatusesToNotInclude = ["draft", "close", "cancel", "reject"];
  return loan && !loanStatusesToNotInclude.includes(loan.loan_state);
}

export function filterVisibleLoans(loans) {
  if (!loans) return [];

  return loans.filter(shouldShowThisLoan);
}

export function filterLoanHistoryList(loans) {
  const shouldShowTheseLoanStatuses = ["close", "cancel", "reject"];
  return loans.filter((loan) =>
    shouldShowTheseLoanStatuses.includes(loan.loan_state)
  );
}

export function generateReceiptImageName({ loanId, installment }) {
  const notFoundLoanId = "no-loan-id";
  const notFoundInstallmentId = i18next.t("loan:status.unknownId");

  if (!installment) return "";

  const currentReceiptRound = installment.number_payment + 1;

  return `R${loanId || notFoundLoanId}_${
    installment.installment_name || notFoundInstallmentId
  }_${currentReceiptRound}`;
}

export function allowDownloadInvoice(installment) {
  const todayPlus20Days = dayjs().add(20, "day");
  const latestInstallmentDate = dayjs(installment?.installment_date);

  return (
    installment?.installment_date &&
    (todayPlus20Days.isSame(latestInstallmentDate) ||
      todayPlus20Days.isAfter(latestInstallmentDate))
  );
}

export function allowMakePayment(installment) {
  const today = dayjs();
  const latestInstallmentDate = dayjs(installment?.installment_date);
  return (
    installment?.installment_date &&
    (today.isSame(latestInstallmentDate) ||
      today.isAfter(latestInstallmentDate) ||
      (today.get("month") === latestInstallmentDate.get("month") &&
        today.get("year") === latestInstallmentDate.get("year")))
  );
}

export function getUnpaidInstallmentSummary(loanInfo) {
  if (!loanInfo) return null;

  if (!loanInfo.installments) return null;

  const unpaidInstallments = loanInfo.installments.filter(
    (installment) =>
      installment.state !== "paid" && allowDownloadInvoice(installment)
  );

  return {
    latestInstallment: unpaidInstallments[unpaidInstallments.length - 1],
    total: unpaidInstallments.reduce(
      (acc, installment) =>
        acc + (installment.lost_paid || installment.total_amount),
      0
    ),
  };
}

export function getLatestInstallment(loanInfo) {
  if (!loanInfo) return null;

  if (!loanInfo.installments) return null;

  const firstInstallmentIndex = 0;

  const isSomeInstallmentsPaid = loanInfo.installments.some(
    (installment) => installment.state === "paid"
  );

  if (isSomeInstallmentsPaid) {
    const lastPaidInstallmentIndex = loanInfo.installments.findLastIndex(
      (installment) => installment.state === "paid"
    );

    return loanInfo.installments[lastPaidInstallmentIndex + 1];
  }

  return loanInfo.installments[firstInstallmentIndex];
}

export function translateInstallmentStatus(installment) {
  const defaultStatus = "";

  if (!installment) return defaultStatus;

  const { state: installmentStatus, pending_transactions } = installment;

  const areAllTransactionsRejected =
    pending_transactions && pending_transactions.length > 0
      ? pending_transactions.every(
          (transaction) => transaction.state === TRANSACTION_STATUS.reject
        )
      : false;

  const isSomeTransactionRejected = pending_transactions?.some(
    (transaction) => transaction.state === TRANSACTION_STATUS.reject
  );

  if (installmentStatus === INSTALLMENT_STATUS.paid) {
    return i18next.t("loan:status.paid");
  } else if (installmentStatus === INSTALLMENT_STATUS.unpaid) {
    return areAllTransactionsRejected
      ? i18next.t("loan:status.failed")
      : i18next.t("loan:status.pending");
  } else if (installmentStatus === INSTALLMENT_STATUS.notfullpaid) {
    return isSomeTransactionRejected
      ? i18next.t("loan:status.partialFailed")
      : i18next.t("loan:status.partialPending");
  } else if (installmentStatus === INSTALLMENT_STATUS.pending) {
    return i18next.t("loan:status.verifying");
  } else {
    return defaultStatus;
  }
}

export function getInstallmentStatusColor(installmentStatus) {
  const defaultColor = "#101828​";
  switch (installmentStatus) {
    case "paid":
      return "#067647";
    case "approve":
      return "#067647";
    case "notfullpaid":
      return "#B93815";
    case "pending":
      return "#965D0D";
    case "reject":
      return "#B93815";
    case "unpaid":
      return "#101828";
    default:
      return defaultColor;
  }
}
